import NiceModal from '@ebay/nice-modal-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { type ChangeOrderRefType, getChangeOrderApprovals } from '@/api/Sherpa/ChangeOrder';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
import ChangeOrderDetails from '@/features/ChangeOrder/Details';
import { EditBudgetModal } from '@/features/ChangeOrder/Summary/ChangeOrderBudget/EditBudget';
import { useBudgetChangeOrderDetails } from '@/hooks/ReactQueryHooks/useChangeOrderDetailsQuery';
import { accountingFormatNumber, formatDate3 } from '@/utils';

export function ChangeOrderBudgetDetails() {
  const { projectId = '', changeOrderId = '' } = useParams();
  const queryClient = useQueryClient();
  const { data } = useBudgetChangeOrderDetails({
    id: Number(changeOrderId),
  });
  const resourceType: ChangeOrderRefType = 'changeOrderBudgetLineItem';

  const { data: approvals } = useQuery(
    [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_APPROVALS, changeOrderId],
    () =>
      getChangeOrderApprovals({
        refType: resourceType,
        refId: Number(changeOrderId),
      }),
    {
      select: (data) => data.data,
    }
  );

  const invalidateQueries = () => {
    queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_CHANGE_ORDER_BUDGET, Number(changeOrderId)]);
    queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_CHANGE_ORDER_APPROVALS]);
    queryClient.invalidateQueries([`${resourceType}-audit`]);
  };

  const handleEdit = () => {
    if (!data) return;

    NiceModal.show(EditBudgetModal, {
      projectId,
      changeOrderId,
      defaultValues: {
        phase: {
          label: data.phase.name,
          value: data.phase.slug,
        },
        lineItem: {
          value: String(data.cOrderTypeId),
          label: data.cOrderTypeName,
          amount: Number(data.lineItem.fromAmount),
        },
        budgetedAmount: data.lineItem.fromAmount,
        proposedAmount: data.lineItem.proposedAmount,
        impact: data.lineItem.budgetImpacted,
        noResponseConsequence: data.consequences,
        notes: data.notes,
        reason: data.reason,
        responseDueBy: formatDate3(data.lineItem.responseDueDate, 'yyyy-mm-dd'),
        recentFiles: approvals?.at(0)?.approvalAsset,
      },
      onSubmitted: invalidateQueries,
    });
  };

  return (
    <ChangeOrderDetails
      title={data?.cOrderTypeName}
      type='budget'
      resourceType={resourceType}
      commentResourceType='ChangeOrderBudgetLineItem'
      onEdit={handleEdit}
      status={data?.status || ''}
      submittedBy={data?.submittedBy}
      additionalInfo={[
        {
          title: 'Why is the change required?',
          value: data?.reason || '',
        },
        {
          title: 'No response consequence',
          value: data?.consequences || '',
        },
        {
          title: 'Notes',
          value: data?.notes || '–',
        },
      ]}
      details={[
        {
          title: 'Original Amount',
          value: accountingFormatNumber({
            num: data?.lineItem.fromAmount || 0,
          }),
        },
        {
          title: 'Response Due By Date',
          value: formatDate3(data?.lineItem.responseDueDate || ''),
        },
        {
          title: 'Proposed Amount',
          value: accountingFormatNumber({
            num: data?.lineItem.proposedAmount || 0,
          }),
        },
        {
          title: 'Days Left to Respond',
          value: String(data?.daysLeft || 0),
        },
        {
          title: '',
          value: '',
        },
        {
          title: 'Impact',
          value: accountingFormatNumber({
            num: data?.lineItem.budgetImpacted || 0,
          }),
        },
      ]}
    />
  );
}
